import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Toast } from 'primereact/toast';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputTextarea } from 'primereact/inputtextarea';
import { AuthContext } from '../../../Header/auth - context';
import { uzmiGresku } from '../../../custom/greske';
import './SaTelefonaTab.css';

/* Ture → Obračun → „Sa telefona" (MB #139).

   Šta je vozač predao sa telefona uz izabranu turu: stanje hladnjače na početku
   i kraju (sati, nivo goriva, slike), kilometraža po putnom listu, dani u vozilu,
   vožnja vikendom i predlog bonusa. To je PREDLOG — „Prihvati" prebacuje bonus u
   bonuse ture i dodaje dane u vozilu kojih u obračunu još nema (koeficijent 1);
   km upisuje u turu samo ako su tamo prazne. „Odbij" traži razlog, koji vozač
   vidi na telefonu. Obračunata tura se ne menja. */

const STATUS = { 0: 'ceka', 1: 'prihvaceno', 2: 'odbijeno' };

const SaTelefonaTab = () => {
    const { t } = useTranslation();
    const auth = useContext(AuthContext);
    const trip = useSelector((state) => state.trip);
    const toast = useRef(null);

    const [unosi, setUnosi] = useState(null);
    const [radi, setRadi] = useState(null);          // id unosa koji se obrađuje
    const [odbijanje, setOdbijanje] = useState(null); // { id, razlog }
    const [slika, setSlika] = useState(null);

    const osnova = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/API\/?$/, '');

    const zovi = useCallback(async (telo) => {
        const res = await fetch(process.env.REACT_APP_BACKEND_URL + '/trip/vozacTura', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + auth.token },
            body: JSON.stringify(telo),
        });
        if (!res.ok) throw res;
        return res.json();
    }, [auth.token]);

    const ucitaj = useCallback(async () => {
        if (!trip) { setUnosi([]); return; }
        setUnosi(null);
        try {
            setUnosi(await zovi({ step: 'WEB_GET', id_putni_nalog: trip }));
        } catch (e) {
            const g = await uzmiGresku(e, t('saTelefona.greskaUcitavanja'));
            toast.current?.show({ severity: 'error', summary: t('common.greska'), detail: g.message, life: 5000 });
            setUnosi([]);
        }
    }, [trip, zovi, t]);

    useEffect(() => { ucitaj(); }, [ucitaj]);

    const obradi = async (telo) => {
        setRadi(telo.id);
        try {
            const d = await zovi(telo);
            toast.current?.show({ severity: 'success', summary: t('common.uspeh'), detail: d.message, life: 3500 });
            setOdbijanje(null);
            ucitaj();
        } catch (e) {
            const g = await uzmiGresku(e, t('saTelefona.greskaObrade'));
            toast.current?.show({ severity: 'error', summary: t('common.greska'), detail: g.message, life: 5000 });
        } finally {
            setRadi(null);
        }
    };

    const broj = (v, dec = 0) => (v == null || v === '' ? '—'
        : Number(v).toLocaleString('sr-RS', { minimumFractionDigits: dec, maximumFractionDigits: dec }));

    const frigoBlok = (u, faza) => {
        const sati = u[`frigo${faza}Sati`];
        if (sati == null) return null;
        const putanja = u[`frigo${faza}Slika`];
        return (
            <div className="st-frigo">
                <div className="st-frigo-naslov">{faza === 'Pocetak' ? t('saTelefona.frigoPocetak') : t('saTelefona.frigoKraj')}</div>
                <div className="st-vrednost">{broj(sati, 1)} h · {t('saTelefona.gorivo')} {u[`frigo${faza}Nivo`]}%</div>
                <div className="st-meta">{u[`frigo${faza}VremeDisplay`]}</div>
                {u[`frigo${faza}Napomena`] && <div className="st-napomena">{u[`frigo${faza}Napomena`]}</div>}
                {putanja && (
                    <button type="button" className="st-slika" onClick={() => setSlika(osnova + putanja)}>
                        <img src={osnova + putanja} alt={t('saTelefona.slikaHladnjace')} />
                    </button>
                )}
            </div>
        );
    };

    if (!trip) return <p className="infoPoruka" style={{ margin: '1em 0' }}>{t('saTelefona.izaberiTuru')}</p>;

    return (
        <div className="sa-telefona">
            <Toast ref={toast} />
            {unosi === null && <p className="st-meta">{t('common.ucitavanje')}</p>}
            {unosi && unosi.length === 0 && <p className="infoPoruka" style={{ margin: '1em 0' }}>{t('saTelefona.nemaUnosa')}</p>}

            {(unosi || []).map((u) => {
                const stanje = STATUS[u.statusObrade] || 'ceka';
                const predjeno = u.pocetnaKm != null && u.zavrsnaKm != null ? u.zavrsnaKm - u.pocetnaKm : null;
                const dani = u.Dani || [];
                const uVozilu = dani.filter((d) => d.uVozilu).length;
                const vikendi = dani.filter((d) => d.vikendVoznja).length;
                return (
                    <Card key={u.id} className="st-karta">
                        <div className="st-zaglavlje">
                            <div>
                                <div className="st-vozac">{u.Vozac}</div>
                                <div className="st-meta">
                                    {u.predatoDisplay ? t('saTelefona.predato', { kada: u.predatoDisplay }) : t('saTelefona.nijePredato')}
                                </div>
                            </div>
                            <span className={`st-status st-status-${stanje}`}>{t(`saTelefona.status_${stanje}`)}</span>
                        </div>

                        {(u.frigoPocetakSati != null || u.frigoKrajSati != null) && (
                            <section className="st-sekcija">
                                <h4>{t('saTelefona.hladnjaca')}</h4>
                                <div className="st-frigo-red">
                                    {frigoBlok(u, 'Pocetak')}
                                    {frigoBlok(u, 'Kraj')}
                                </div>
                            </section>
                        )}

                        {u.predatoDisplay && (
                            <>
                                <section className="st-sekcija st-brojke">
                                    <div><span>{t('saTelefona.pocetnaKm')}</span><b>{broj(u.pocetnaKm)}</b></div>
                                    <div><span>{t('saTelefona.zavrsnaKm')}</span><b>{broj(u.zavrsnaKm)}</b></div>
                                    <div><span>{t('saTelefona.predjeno')}</span><b>{predjeno != null ? `${broj(predjeno)} km` : '—'}</b></div>
                                    <div><span>{t('saTelefona.daniUVozilu')}</span><b>{uVozilu}</b></div>
                                    <div><span>{t('saTelefona.vikendi')}</span><b>{vikendi}</b></div>
                                    <div><span>{t('saTelefona.bonus')}</span><b>{u.bonusIznosEur ? `${broj(u.bonusIznosEur, 2)} EUR` : '—'}</b></div>
                                </section>
                                {u.bonusNapomena && <p className="st-napomena"><b>{t('saTelefona.bonusZaSta')}:</b> {u.bonusNapomena}</p>}
                                {u.napomena && <p className="st-napomena"><b>{t('saTelefona.napomena')}:</b> {u.napomena}</p>}

                                {dani.length > 0 && (
                                    <section className="st-sekcija">
                                        <h4>{t('saTelefona.dani')}</h4>
                                        <div className="st-dani">
                                            {dani.map((d) => (
                                                <div key={d.datum}
                                                    className={'st-dan' + (d.uVozilu ? ' st-dan-da' : ' st-dan-ne') + (d.vikendVoznja ? ' st-dan-vikend' : '')}
                                                    title={[
                                                        d.uVozilu ? t('saTelefona.uVozilu') : t('saTelefona.nijeUVozilu'),
                                                        d.vikendVoznja ? t('saTelefona.vozioVikend') : null,
                                                        d.uObracunu ? t('saTelefona.vecUObracunu') : null,
                                                    ].filter(Boolean).join(' · ')}>
                                                    <span className="st-dan-datum">{d.datumDisplay}</span>
                                                    <span className="st-dan-oznaka">{d.uVozilu ? '✓' : '—'}{d.vikendVoznja ? ' V' : ''}</span>
                                                    {d.uObracunu ? <span className="st-dan-obracun">{t('saTelefona.uObracunuKratko')}</span> : null}
                                                </div>
                                            ))}
                                        </div>
                                        <p className="st-meta">{t('saTelefona.legenda')}</p>
                                    </section>
                                )}

                                {u.statusObrade === 2 && u.razlogOdbijanja && (
                                    <p className="st-napomena st-odbijeno"><b>{t('saTelefona.razlog')}:</b> {u.razlogOdbijanja}</p>
                                )}
                                {u.statusObrade !== 0 && u.Obradio && (
                                    <p className="st-meta">{t('saTelefona.obradio', { ko: u.Obradio, kada: u.obradjenoDisplay })}</p>
                                )}

                                {u.statusObrade === 0 && (
                                    <div className="st-dugmad">
                                        <Button label={t('saTelefona.odbij')} icon="pi pi-times" severity="danger" outlined
                                            disabled={radi === u.id} onClick={() => setOdbijanje({ id: u.id, razlog: '' })} />
                                        <Button label={t('saTelefona.prihvati')} icon="pi pi-check" severity="success"
                                            loading={radi === u.id} onClick={() => obradi({ step: 'WEB_PRIHVATI', id: u.id })} />
                                    </div>
                                )}
                            </>
                        )}
                    </Card>
                );
            })}

            <Dialog header={t('saTelefona.odbijNaslov')} visible={!!odbijanje} style={{ width: '32rem', maxWidth: '96vw' }}
                onHide={() => setOdbijanje(null)}>
                {odbijanje && (
                    <div className="st-odbij-forma">
                        <label htmlFor="st_razlog">{t('saTelefona.razlog')}</label>
                        <InputTextarea id="st_razlog" rows={3} autoResize value={odbijanje.razlog}
                            onChange={(e) => setOdbijanje({ ...odbijanje, razlog: e.target.value })}
                            placeholder={t('saTelefona.razlogPh')} />
                        <div className="st-dugmad">
                            <button type="button" className="btn btn-secondary" onClick={() => setOdbijanje(null)}>{t('common.otkazi')}</button>
                            <button type="button" className="btn btn-danger" disabled={!odbijanje.razlog.trim() || radi === odbijanje.id}
                                onClick={() => obradi({ step: 'WEB_ODBIJ', id: odbijanje.id, razlog: odbijanje.razlog.trim() })}>
                                {t('saTelefona.odbij')}
                            </button>
                        </div>
                    </div>
                )}
            </Dialog>

            <Dialog header={t('saTelefona.slikaHladnjace')} visible={!!slika} style={{ width: '40rem', maxWidth: '96vw' }}
                onHide={() => setSlika(null)}>
                {slika && <img src={slika} alt={t('saTelefona.slikaHladnjace')} className="st-slika-velika" />}
            </Dialog>
        </div>
    );
};

export default SaTelefonaTab;
